import { useState } from 'react';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Contact, NotificationChannel } from '@/core/notifications/model';
import { buildContactConfig, isContactFormValid } from '@/core/notifications/model/contact-form';
import ContactFormFields from '@/core/notifications/components/settings/contact-form-fields';

// The "+ Add new contact" affordance ChannelPicker renders through its
// `renderAddContact` slot: same call site and same 409/422 toasts as
// add-contact-modal.tsx, but inline and scoped to a single channel so the user
// never has to leave the wizard. ChannelPicker only offers this slot for
// channels whose `requiresVerification` is NOT true, so the contact this
// creates is guaranteed to come back already verified — the caller can assign
// it as this subscription's channel contact immediately, no waiting.
export default function InlineContactRow({
  channel,
  onAdded,
}: {
  channel: NotificationChannel;
  // `contacts` is omitted when the post-add refresh GET failed — the caller
  // must NOT treat an omitted list as "the user has no contacts" and wipe its
  // already-loaded contacts state. `addedContact` is the (already verified)
  // contact that was just created.
  onAdded: (channelKey: string, contacts: Contact[] | undefined, addedContact: Contact) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const isValid = isContactFormValid(channel, fieldValues);

  const close = () => {
    setExpanded(false);
    setFieldValues({});
    setLabel('');
  };

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      const added = await notificationApi.addContact(
        channel.key,
        buildContactConfig(channel, fieldValues),
        channel.supportsLabel ? label.trim() || undefined : undefined,
      );
      toast.success('Contact added');
      // If this refresh fails, fall through with `contacts` omitted rather than
      // an empty array — an empty array would tell the wizard "you have zero
      // contacts" and wipe its already-loaded list, disabling every previously
      // verified channel.
      const contacts = await notificationApi.getContacts().catch(() => undefined);
      onAdded(channel.key, contacts, added);
      close();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('This contact already exists for this channel');
      } else if (error?.response?.status === 422) {
        toast.error('Some required fields are missing or invalid');
      } else {
        toast.error('Could not add the contact');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!expanded) {
    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        style={{ fontSize: 12 }}
        onClick={() => setExpanded(true)}
      >
        + Add new {channel.displayName} contact
      </button>
    );
  }

  return (
    <div
      className="mt-2 p-3 rounded"
      style={{ border: '1px solid var(--bs-primary-200)', background: 'var(--bs-tertiary-bg)' }}
    >
      <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
        New {channel.displayName} contact
      </div>
      {/* the channel is fixed and already named in the heading above, so the
          generic channel select from ContactFormFields would just be noise here */}
      <ContactFormFields
        channels={[channel]}
        channelKey={channel.key}
        channel={channel}
        fieldValues={fieldValues}
        label={label}
        onChannelChange={() => {}}
        onFieldChange={handleFieldChange}
        onLabelChange={setLabel}
        hideChannelSelect
      />
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={close}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={handleSave}
          disabled={!isValid || saving}
        >
          {saving ? 'Saving…' : 'Save contact'}
        </button>
      </div>
    </div>
  );
}
